import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "@/store";
import { fetchSetting, createSetting } from "@/store/modules/settings/thunks";
import { usePermission } from "@/hooks";
import { useAuth } from "@/context/AuthContext";

interface UseSettingReturn {
  loading: boolean;
  submitting: boolean;
  bannerPreview: string | null;
  logoPreview: string | null;
  scheduleBannerPreviews: string[];
  platformLogoPreviews: string[];
  qrcodePreviews: string[];
  existing: any | null;
  removeFileIds: number[];
  selectBanner: (file: File | null) => void;
  selectLogo: (file: File | null) => void;
  addScheduleBanners: (files: FileList | null) => void;
  addPlatformLogos: (files: FileList | null) => void;
  addQrcodes: (files: FileList | null) => void;
  removeExistingFile: (id: number) => void;
  undoRemoveExistingFile: (id: number) => void;
  removeNewFile: (
    group: "schedule_banners" | "platform_logos" | "qrcodes",
    index: number,
  ) => void;
  submitMedia: () => Promise<void>;
  canEdit: boolean;
  hasChanges: boolean;
  clearChanges: () => void;
}

// Helper to extract first file id by type
const findFileIdByType = (setting: any, type: string): number | undefined => {
  if (!setting) return undefined;
  // Some APIs may expose consolidated files array
  if (Array.isArray(setting.files)) {
    const f = setting.files.find((f: any) => f.type === type);
    return f?.id;
  }
  // Or nested meta objects
  const meta = (
    type === "banner" ? setting.banner_image : setting.logo_image
  ) as any;
  return meta?.id;
};

const buildAbsolute = (raw?: string | null): string | null => {
  if (!raw) return null;
  // already absolute
  if (/^https?:\/\//i.test(raw)) return raw;
  // ensure leading slash
  const path = raw.startsWith("/") ? raw : `/${raw}`;
  // Try env API URL; fallback to window origin (only client-side)
  const base =
    (typeof process !== "undefined" && process.env.NEXT_PUBLIC_API_IMAGE_URL) ||
    (typeof window !== "undefined" ? window.location.origin : "");
  return base ? `${base.replace(/\/$/, "")}${path}` : raw;
};

const urlFrom = (obj: any): string | null => {
  if (!obj) return null;
  if (typeof obj === "string") return buildAbsolute(obj);
  return buildAbsolute(obj.url) || buildAbsolute(obj.path) || null;
};

function useSettingInternal(): UseSettingReturn {
  const dispatch: AppDispatch = useDispatch();
  const { hasPermission } = usePermission();
  const { establishment, baseURL } = useAuth();
  const canEdit = hasPermission("create setting");
  const canRead = hasPermission("read setting");

  const { setting, loading } = useSelector(
    (state: RootState) => state.settings,
  );
  const existing = useMemo(
    () => (setting?.data?.length ? setting.data[0] : null),
    [setting?.data],
  );

  // Local new files
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [scheduleBannerFiles, setScheduleBannerFiles] = useState<File[]>([]);
  const [platformLogoFiles, setPlatformLogoFiles] = useState<File[]>([]);
  const [qrcodeFiles, setQrcodeFiles] = useState<File[]>([]);
  const [removeFileIds, setRemoveFileIds] = useState<number[]>([]);
  const [submitting, setSubmitting] = useState(false);

  // Fetch only once (guard against React StrictMode double invoke & function identity changes)
  const fetchedRef = useRef(false);
  useEffect(() => {
    // Aguarda establishment e baseURL estarem prontos antes de fazer requests
    if (!baseURL || !establishment) return;
    if (!canRead) return;
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    dispatch(fetchSetting({} as any));
  }, [dispatch, canRead, baseURL, establishment]);

  const bannerPreview = useMemo(() => {
    if (bannerFile) return URL.createObjectURL(bannerFile);
    const direct =
      urlFrom(existing?.banner_url) || urlFrom(existing?.banner_image);
    if (direct) return direct;
    // fallback: search in files array type banner
    const bannerFileMeta = existing?.files?.find?.(
      (f: any) => f.type === "banner",
    );
    return urlFrom(bannerFileMeta?.url || bannerFileMeta?.path);
  }, [bannerFile, existing]);

  const logoPreview = useMemo(() => {
    if (logoFile) return URL.createObjectURL(logoFile);
    const direct = urlFrom(existing?.logo_url) || urlFrom(existing?.logo_image);
    if (direct) return direct;
    const logoFileMeta = existing?.files?.find?.((f: any) => f.type === "logo");
    return urlFrom(logoFileMeta?.url || logoFileMeta?.path);
  }, [logoFile, existing]);

  const scheduleBannerPreviews = useMemo(() => {
    const existingOnes: string[] = (existing?.schedule_banners || [])
      .filter(Boolean)
      .map((f: any) => f.url || f.path)
      .filter(Boolean);
    const newOnes = scheduleBannerFiles.map((f) => URL.createObjectURL(f));
    return [...existingOnes, ...newOnes];
  }, [existing, scheduleBannerFiles]);

  const platformLogoPreviews = useMemo(() => {
    const existingUrls: string[] = (
      existing?.platform_logos_urls ||
      existing?.platform_logos ||
      []
    )
      .map((u: any) => (typeof u === "string" ? u : u.url || u.path))
      .filter(Boolean);
    const newOnes = platformLogoFiles.map((f) => URL.createObjectURL(f));
    return [...existingUrls, ...newOnes];
  }, [existing, platformLogoFiles]);

  const qrcodePreviews = useMemo(() => {
    const existingUrls: string[] = (
      existing?.qrcodes_urls ||
      existing?.qrcodes ||
      []
    )
      .map((u: any) => (typeof u === "string" ? u : u.url || u.path))
      .filter(Boolean);
    const newOnes = qrcodeFiles.map((f) => URL.createObjectURL(f));
    return [...existingUrls, ...newOnes];
  }, [existing, qrcodeFiles]);

  const selectBanner = useCallback((file: File | null) => {
    setBannerFile(file);
  }, []);

  const selectLogo = useCallback((file: File | null) => {
    setLogoFile(file);
  }, []);

  const addScheduleBanners = useCallback((files: FileList | null) => {
    if (!files) return;
    setScheduleBannerFiles((prev) => [...prev, ...Array.from(files)]);
  }, []);

  const addPlatformLogos = useCallback((files: FileList | null) => {
    if (!files) return;
    setPlatformLogoFiles((prev) => [...prev, ...Array.from(files)]);
  }, []);

  const addQrcodes = useCallback((files: FileList | null) => {
    if (!files) return;
    setQrcodeFiles((prev) => [...prev, ...Array.from(files)]);
  }, []);

  const removeExistingFile = useCallback((id: number) => {
    setRemoveFileIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
  }, []);

  const undoRemoveExistingFile = useCallback((id: number) => {
    setRemoveFileIds((prev) => prev.filter((i) => i !== id));
  }, []);

  const removeNewFile = useCallback(
    (
      group: "schedule_banners" | "platform_logos" | "qrcodes",
      index: number,
    ) => {
      if (group === "schedule_banners") {
        setScheduleBannerFiles((prev) => prev.filter((_, i) => i !== index));
      } else if (group === "platform_logos") {
        setPlatformLogoFiles((prev) => prev.filter((_, i) => i !== index));
      } else {
        setQrcodeFiles((prev) => prev.filter((_, i) => i !== index));
      }
    },
    [],
  );

  const hasChanges = useMemo(() => {
    return !!(
      bannerFile ||
      logoFile ||
      scheduleBannerFiles.length ||
      platformLogoFiles.length ||
      qrcodeFiles.length ||
      removeFileIds.length
    );
  }, [
    bannerFile,
    logoFile,
    scheduleBannerFiles,
    platformLogoFiles,
    qrcodeFiles,
    removeFileIds,
  ]);

  const clearChanges = useCallback(() => {
    setBannerFile(null);
    setLogoFile(null);
    setScheduleBannerFiles([]);
    setPlatformLogoFiles([]);
    setQrcodeFiles([]);
    setRemoveFileIds([]);
  }, []);

  const submitMedia = useCallback(async () => {
    if (!canEdit || submitting) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      // Substitution singles
      if (bannerFile) fd.append("banner_image", bannerFile);
      if (logoFile) fd.append("logo_image", logoFile);
      // Additive arrays
      scheduleBannerFiles.forEach((f) => fd.append("schedule_banners[]", f));
      platformLogoFiles.forEach((f) => fd.append("platform_logos[]", f));
      qrcodeFiles.forEach((f) => fd.append("qrcodes[]", f));
      removeFileIds.forEach((id) => fd.append("remove_file_ids[]", String(id)));

      // Attempt to include existing base booleans if present (avoid accidentally nulling). Only append those present in existing.
      ["wifi", "parking", "accessibility", "accepts_children"].forEach(
        (key) => {
          if (existing && typeof existing[key] !== "undefined") {
            fd.append(key, existing[key] ? "1" : "0");
          }
        },
      );

      await dispatch(createSetting(fd as any));
      await dispatch(fetchSetting({} as any));
      clearChanges();
    } finally {
      setSubmitting(false);
    }
  }, [
    bannerFile,
    logoFile,
    scheduleBannerFiles,
    platformLogoFiles,
    qrcodeFiles,
    removeFileIds,
    canEdit,
    dispatch,
    existing,
    submitting,
    clearChanges,
  ]);

  return {
    loading,
    submitting,
    bannerPreview,
    logoPreview,
    scheduleBannerPreviews,
    platformLogoPreviews,
    qrcodePreviews,
    existing,
    removeFileIds,
    selectBanner,
    selectLogo,
    addScheduleBanners,
    addPlatformLogos,
    addQrcodes,
    removeExistingFile,
    undoRemoveExistingFile,
    removeNewFile,
    submitMedia,
    canEdit,
    hasChanges,
    clearChanges,
  };
}

export default useSettingInternal;
